import CloseIcon from '@mui/icons-material/Close';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { Box, Button, IconButton, List, ListItem, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { ReactComponent as MoneyhoonAsteriskCircled } from 'assets/icons/MoneyhoonAsteriskCircled.svg';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import {
  ChatMessage,
  ChatMessageType,
  SortDirection,
  useAddMessageToChatMutation,
  useChatsQuery,
  useInitChatMutation,
} from 'generated/graphql';
import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { styles } from './ChatWindow.styles';

interface ChatDialogProps {
  onClose: () => void;
}

export const ChatDialog: React.FC<ChatDialogProps> = ({ onClose }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const { t } = useTranslation();
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const { data, loading, refetch } = useChatsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated,
    variables: {
      sortInput: { by: [{ direction: SortDirection.Desc, fieldPath: 'creationTimestamp' }] },
    },
  });

  const latestChat = data?.chats.items?.[0];

  useEffect(() => {
    setMessages(_.sortBy(latestChat?.messages, 'creationTimestamp') ?? []);
  }, [latestChat]);
  useEffect(scrollToBottom, [messages]);

  const [initChat, { loading: loadingInitChat }] = useInitChatMutation();
  const [addMessageToChat, { loading: loadingAddMessageToChat }] = useAddMessageToChatMutation();

  const sendMessage = async (newMessageString: string) => {
    if (!newMessageString.trim()) return;
    const newMessage = { text: newMessageString, type: ChatMessageType.User };
    setMessages([...messages, { ...newMessage, creationTimestamp: new Date(), id: '' }]);
    setInputMessage('');

    if (data?.chats.totalItems == 0) {
      await initChat({
        variables: {
          message: newMessage,
        },
      });
    } else {
      await addMessageToChat({
        variables: {
          id: latestChat?.id ?? '',
          message: newMessage,
        },
      });
    }
    await refetch();
  };

  const handleFAQClick = (question: string) => {
    sendMessage(question);
  };

  const questions = [
    t('homepage.chat.faq.questions.1'),
    t('homepage.chat.faq.questions.2'),
    t('homepage.chat.faq.questions.3'),
  ];

  return (
    <Paper elevation={3} sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton
          aria-label={t('common:close')}
          color="secondaryButton"
          onClick={() => onClose()}
          size="medium"
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack sx={styles.content}>
        <List sx={styles.list}>
          {!loading && !latestChat ? (
            <Stack direction="column" gap={1.5}>
              <MoneyhoonAsteriskCircled height={64} width={64} />
              <Typography variant="bodyStrongXL">{t('homepage.chat.title')}</Typography>
              <Typography color="text.secondary" variant="bodyM">
                {t('homepage.chat.subtitle')}
              </Typography>
              <Box mt={1}>
                <Typography color="text.secondary" variant="bodyM">
                  {t('homepage.chat.faq.title')}
                </Typography>
              </Box>
              <Stack alignItems="flex-start" display="flex" flexDirection="column" gap={1.5}>
                {questions.map((question, index) => (
                  <Box key={index} onClick={() => handleFAQClick(question)} sx={styles.faq}>
                    <Typography variant="bodyM">
                      <SubdirectoryArrowRightIcon fontSize="inherit" />
                      {question}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          ) : (
            messages?.map((message, index) => (
              <ListItem
                key={index}
                sx={{
                  ...styles.listItem,
                  justifyContent: message.type == ChatMessageType.User ? 'flex-end' : 'flex-start',
                }}
              >
                {message.type == ChatMessageType.User ? (
                  <Box sx={styles.userMessage}>
                    <Typography variant="bodyM">{message.text}</Typography>
                  </Box>
                ) : (
                  <Stack
                    alignItems="top"
                    direction="row"
                    display="flex"
                    gap={2}
                    justifyContent="space-between"
                    sx={styles.systemMessage}
                  >
                    <Box height={40} width={40}>
                      <MoneyhoonAsteriskCircled />
                    </Box>
                    <Box sx={styles.markdownContainer}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
                    </Box>
                  </Stack>
                )}
              </ListItem>
            ))
          )}
          {(loadingInitChat || loadingAddMessageToChat) && (
            <ListItem
              sx={{
                ...styles.listItem,
                justifyContent: 'flex-start',
              }}
            >
              <Stack
                alignItems="center"
                direction="row"
                display="flex"
                gap={2}
                justifyContent="space-between"
                sx={styles.systemMessage}
              >
                <Box height={40} width={40}>
                  <MoneyhoonAsteriskCircled />
                </Box>
                <Box height={20} width={20}>
                  <Skeleton variant="circular" />
                </Box>
              </Stack>
            </ListItem>
          )}
          <Box ref={messagesEndRef} />
        </List>

        <Stack direction="column" gap={1}>
          <Stack
            component="form"
            direction="row"
            display="flex"
            gap={1}
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(inputMessage);
            }}
          >
            <TextField
              fullWidth
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={t('homepage.chat.placeholder')}
              size="small"
              value={inputMessage}
              variant="outlined"
            />
            <Button
              aria-label={t('homepage.chat.submit')}
              color="primary"
              disabled={loadingInitChat || loadingAddMessageToChat}
              type="submit"
            >
              <Typography color="white" variant="bodyL">
                {t('homepage.chat.submit')}
              </Typography>
            </Button>
          </Stack>
          <Typography color="text.secondary" variant="bodyXS">
            {t('homepage.chat.disclaimer')}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};
